/**
 * The only place the app talks to the API.
 *
 * Every call carries the Clerk session token. Nothing here decides what the user
 * may do — the Worker does that, and `plan` in the response is a *display* value.
 * See src/middleware/requirePlan.ts: entitlement is never trusted from the client.
 */

/**
 * The leak report's segment shape is declared in `report-view.ts`, not here.
 *
 * That module sorts and formats segments and has to be testable outside Vite; this
 * one reads `import.meta.env`, which only exists under Vite. So the type lives there
 * and is re-exported here, and callers see a single name.
 */
import type { LeakSegment } from "./report-view";
import type { UnlockId } from "@config/plans";
export type { LeakSegment };

const BASE = import.meta.env.VITE_API_URL as string;

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly body: unknown) {
    super(`${status} ${code}`);
  }

  /** What to actually show someone. Error codes are for us, not for them. */
  get humane(): string {
    switch (this.code) {
      case "profile_unavailable":
        return "We signed you in, but couldn't load your account. That's a problem on our side, not yours.";
      case "invalid_token":
        return "Your session expired. Sign in again.";
      case "account_deleted":
        return "This account was deleted. If you did not expect that, get in touch and we will look into it.";
      case "rate_limited":
        return "Too many requests just now. Wait a moment and try again.";
      case "daily_limit_reached":
        return "You've used today's spots. They reset at midnight UTC — or go Pro for unlimited.";
      case "upgrade_required":
        return "That table size is part of Pro.";
      case "coming_soon":
        return "We haven't solved that combination yet, so it isn't available.";
      case "unknown_combination":
        return "There's no chart for that combination.";
      case "spot_not_open":
        return "That spot has expired. Start a new one.";
      default:
        return this.status >= 500
          ? "Something broke on our end. Try again in a moment."
          : "That didn't work. Try again.";
    }
  }
}

type TokenFn = () => Promise<string | null>;

async function call<T>(getToken: TokenFn, path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const code =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : "request_failed";
    throw new ApiError(res.status, code, body);
  }
  return body as T;
}

/* ------------------------------------------------------------------ types --- */

export type PlanId = "free" | "pro";
export type PlanStatus = "trialing" | "active" | "past_due" | "canceled" | "expired" | null;
export type PlaysWhere = "home" | "live_casino" | "online_cash" | "online_mtt";

export interface Me {
  user: {
    id: string;
    email: string;
    role: "user" | "admin";
    consentAnalytics: boolean;
    playsWhere: PlaysWhere | null;
    stakes: string | null;
  };
  plan: {
    id: PlanId;
    label: string;
    capabilities: string[];
    tableSizes: number[];
    status: PlanStatus;
    expiresAt: number | null;
    trialEndsAt: number | null;
  };
  usage: { used: number; limit: number | null; remaining: number | null; day: string };
  /**
   * Buster Bucks. One fetch paints every header — the saloon, the room, the table.
   *
   * Optional on the type because a browser can be talking to a Worker deployed before
   * Phase 4, and a header that throws because a field is missing is a worse failure
   * than one that shows nothing.
   */
  wallet?: { balance: number };
  pricing: {
    monthly: { amountCents: number; interval: "month" };
    annual: { amountCents: number; interval: "year" };
    trialDays: number;
    founderCoupon: string;
    founderSeats: number;
  };
}

/* ---------------------------------------------------------- Buster Bucks --- */

/** One movement in the ledger, as `/api/me/wallet` returns it. */
export interface LedgerEntry {
  id: string;
  delta: number;
  balance_after: number;
  /** `stake` is the refill flag every leaderboard filters out. */
  reason: "grant" | "buyin" | "settle" | "stake" | "admin_adjust";
  ref_id: string | null;
  created_at: number;
}

export interface WalletView {
  balance: number;
  floor: number;
  stakeTo: number;
  canStake: boolean;
  recent: LedgerEntry[];
}

/**
 * What a settlement post reports back.
 *
 * `wallet` is a sub-object rather than fields on the reply because the two halves can
 * disagree: a sitting can be stored and settle nothing (no receipt), or settle and not
 * be stored (analytics consent off). `ok: false` with `already_settled` is a retry
 * doing its job, not an error — the credit landed the first time.
 */
export interface SettleReport {
  ok: boolean;
  reason?: string;
  credited?: number;
  buyin?: number;
  balance?: number;
}

export interface SessionSaved {
  ok: true;
  stored: boolean;
  reason?: string;
  id?: string;
  wallet?: SettleReport;
}

/* ----------------------------------------------------------------- charts --- */

export interface ChartKeyParts {
  format: string;
  variant?: string | null;
  tableSize: number;
  stackDepth: number;
  icmContext?: string | null;
  payoutPreset?: string | null;
  bountyMode?: string | null;
}

export interface CoverageRow {
  chart_key: string;
  format: string;
  variant: string | null;
  table_size: number;
  stack_depth: number;
  icm_context: string | null;
  payout_preset: string | null;
  bounty_mode: string | null;
  status: "live" | "coming_soon" | "failed";
  source: "solved" | "modeled";
}

export interface ChartMeta {
  key: string;
  status: string;
  source: "solved" | "modeled";
  method: string | null;
  iterations: number | null;
  exploitability: number | null;
  /**
   * How far the MODEL is from the game, as a fraction of the pot — a different
   * quantity from `exploitability`, which is how far the solve is from equilibrium
   * inside that model. Null means unmeasured, and unmeasured is not zero.
   */
  abstractionError: number | null;
  /** "gold" | "silver" | null — the metal, decided server-side from the figure above. */
  purity: "gold" | "silver" | null;
  solvedAt: number | null;
}

export interface NodeSummary {
  path: string;
  actor: string;
  street: string;
  potBb: number;
  actions: string[];
}

export interface ChartIndex {
  key: string;
  tableSize: number;
  stackDepth: number;
  evUnit?: string;
  nodes: NodeSummary[];
}

export interface HandStrategy {
  freqs: number[];
  evChips: number[];
  evIcm: number[] | null;
  evBounty: number[] | null;
  weight?: number;
  indifferent?: boolean;
}

export interface ChartNode {
  path: string;
  street: string;
  board: string[];
  actor: string;
  potBb: number;
  stacksBb: Record<string, number>;
  actions: { id: string; label: string; size?: number; sizeUnit?: string }[];
  strategy: Record<string, HandStrategy>;
}

export type Usage = Me["usage"];

export interface Grade {
  index: number;
  /** The action the equilibrium prefers. Null where the hand is indifferent. */
  best: string | null;
  correct: boolean;
  indifferent: boolean;
  evLost: number;
  evLostNormalized: number;
  /** What each action costs, index-aligned to the node's actions. */
  costs: number[];
}

/** Query string for a chart key. Absent parts are omitted, never sent as "null". */
function chartQuery(p: ChartKeyParts, node?: string): string {
  const q = new URLSearchParams();
  q.set("format", p.format);
  q.set("tableSize", String(p.tableSize));
  q.set("stackDepth", String(p.stackDepth));
  if (p.variant) q.set("variant", p.variant);
  if (p.icmContext) q.set("icmContext", p.icmContext);
  if (p.payoutPreset) q.set("payoutPreset", p.payoutPreset);
  if (p.bountyMode) q.set("bountyMode", p.bountyMode);
  if (node) q.set("node", node);
  return q.toString();
}

/* --------------------------------------------------------------- endpoints --- */

/* ------------------------------------------------------------------ admin --- */

export interface AdminMetrics {
  users: number;
  /** Everyone with Pro access, however they got it. */
  pro: number;
  /** People who actually paid. The proof-of-concept number. */
  paying: number;
  /** People who were given access. Not customers. */
  gifted: number;
  giftSeatsUsed: number;
  giftSeatsMax: number;
  trialing: number;
  spots7d: number;
  trialToPaid: number | null;
}

export interface AdminUser {
  id: string;
  email: string;
  role: string;
  plan: string;
  plan_status: string | null;
  plan_expires_at: number | null;
  trial_ends_at: number | null;
  created_at: number;
  deleted_at: number | null;
  /**
   * Which entitlement is granting access: `pro_monthly` / `pro_annual` means they
   * paid, `gift` is a friends-and-family seat, `comp` a dated admin grant. Null means
   * no live entitlement. `plan` alone cannot tell these apart, and the difference
   * between a customer and a friend is the whole reason this screen exists.
   */
  grant_product: string | null;
}

export const api = {
  me: (t: TokenFn) => call<Me>(t, "/api/me"),

  updateProfile: (t: TokenFn, body: { playsWhere?: PlaysWhere; stakes?: string }) =>
    call<{ ok: true }>(t, "/api/me/profile", { method: "PUT", body: JSON.stringify(body) }),

  setConsent: (t: TokenFn, analytics: boolean) =>
    call<{ ok: true; analytics: boolean }>(t, "/api/me/consent", {
      method: "PUT",
      body: JSON.stringify({ analytics }),
    }),

  checkout: (t: TokenFn, interval: "month" | "year", coupon?: string) =>
    call<{ url: string }>(t, "/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ interval, ...(coupon ? { coupon } : {}) }),
    }),

  portal: (t: TokenFn) => call<{ url: string }>(t, "/api/billing/portal", { method: "POST" }),

  /**
   * PHASE 9: the one-time unlocks this account holds — Poker Pro includes both, and a
   * free account holds what it bought (`config/plans.ts`, `unlocksFor`).
   */
  unlocks: (t: TokenFn) =>
    call<{ owned: UnlockId[]; unlocks: UnlockId[]; includedInPlan: boolean }>(t, "/api/billing/unlocks"),

  /** Stripe Checkout for one unlock, paid once. 409 when the plan already includes it. */
  checkoutUnlock: (t: TokenFn, product: UnlockId) =>
    call<{ url: string }>(t, "/api/billing/checkout/unlock", {
      method: "POST",
      body: JSON.stringify({ product }),
    }),

  /** Everything the registry knows about. Free — it carries no strategy. */
  coverage: (t: TokenFn) => call<{ coverage: CoverageRow[] }>(t, "/api/charts/coverage"),

  /**
   * The list of decisions a chart contains. Also free: it is the same information
   * the coverage endpoint gives, one level finer, and it holds no strategy.
   */
  chartIndex: (t: TokenFn, p: ChartKeyParts) =>
    call<{ meta: ChartMeta; index: ChartIndex; usage: Usage }>(t, `/api/charts?${chartQuery(p)}`),

  /**
   * One decision, with its strategy. THIS is what costs a spot.
   *
   * The `receipt` it returns is a signed proof that this user was served this node.
   * Grading refuses without it, because the per-action costs a verdict carries ARE the
   * strategy, and an ungated grading route is a free read of the whole library.
   */
  chartNode: (t: TokenFn, p: ChartKeyParts, node: string) =>
    call<{ meta: ChartMeta; node: ChartNode; usage: Usage; receipt: string }>(
      t, `/api/charts?${chartQuery(p, node)}`),

  /**
   * Post what you did; the server says how it went. The verdict is computed from the
   * chart on the server — the client cannot score itself.
   */
  grade: (t: TokenFn, decisions: unknown[]) =>
    call<{ ok: true; stored: number; grades: Grade[] }>(t, "/api/decisions", {
      method: "POST",
      body: JSON.stringify({ decisions }),
    }),

  /**
   * One write for a whole session of poker, posted when the player leaves the table.
   *
   * Not one per hand and not one per action: the game runs entirely in the browser
   * precisely so that it costs nothing to play, and an endpoint called on every
   * decision would undo that in an afternoon.
   */
  saveGameSession: (t: TokenFn, body: unknown) =>
    call<SessionSaved>(t, "/api/game/session", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  gameSessions: (t: TokenFn) =>
    call<{ sessions: GameSessionRow[]; totals: GameTotals | null }>(t, "/api/game/sessions"),

  /**
   * A blackjack sitting, on the same one-write-when-you-leave contract.
   *
   * The room is free for everyone and the analysis is never gated, so this call is
   * about the RECORD, not the numbers: a signed-out visitor plays the identical game
   * and simply has nothing posted. `stored: false` comes back for a player who has
   * not consented to analytics or who dealt without making a decision, and the room
   * says so rather than pretending it saved something.
   */
  /* ------------------------------------------------------------ Buster Bucks --- */

  /** The wallet card: the balance and the last twenty movements. */
  wallet: (t: TokenFn) =>
    call<WalletView>(t, "/api/me/wallet"),

  /**
   * The Barkeep. He refuses at 500 and above, which is a 409 and not an error to
   * apologise for — the client asks the wallet's `canStake` before offering it.
   */
  stake: (t: TokenFn) =>
    call<{ ok: true; staked: number; balance: number }>(t, "/api/me/wallet/stake", {
      method: "POST", body: JSON.stringify({}),
    }),

  /**
   * Sit down. THE DEBIT IS THIS CALL: the server takes the buy-in and hands back a
   * signed ticket, and there is no way to be seated without it having done so.
   *
   * `receipt` is the ticket this sitting already has, for a rebuy — a blackjack second
   * rack, a cash top-up. The server keeps the nonce and adds to the total, so one
   * sitting settles once however many times it was bought into.
   *
   * A 402 carries `insufficient_bucks` and the balance, which is what the card needs to
   * say what the seat costs and what you have.
   */
  seatReceipt: (t: TokenFn, body: {
    which: "poker" | "blackjack"; buyin: number; seats: number; bigBlind: number; receipt?: string;
  }) =>
    call<{ ok: true; receipt: string; buyin: number; nonce: string; balance: number }>(
      t, "/api/game/receipt", { method: "POST", body: JSON.stringify(body) }),

  saveBlackjackSitting: (t: TokenFn, body: unknown) =>
    call<SessionSaved>(t, "/api/game/blackjack", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  blackjackSittings: (t: TokenFn) =>
    call<{ sittings: BlackjackSittingRow[]; totals: BlackjackTotals | null }>(t, "/api/game/blackjack"),

  /**
   * The segmented trainer report: where the avoidable loss actually sits.
   *
   * Distinct from `gameSessions`, which is the poker table's own history. This one
   * reads GRADED decisions against solved charts, so it is the half of the report
   * that is allowed to use the word "solved" — and the half that is gated.
   *
   * The server decides what a free account may see and withholds the rest before it
   * reaches the browser; `gated` tells the UI which shape came back, it does not
   * cause the gating.
   */
  leaks: (t: TokenFn) => call<LeakReportResponse>(t, "/api/me/leaks"),

  health: () => fetch(`${BASE}/api/health`).then((r) => r.json() as Promise<{ ok: boolean }>),

  /* -------------------------------------------------------------- admin --- */
  // Every one of these is refused by the Worker unless the caller is an admin. The
  // screen hiding itself is a courtesy, not the control.
  adminMetrics: (t: TokenFn) => call<AdminMetrics>(t, "/api/admin/metrics"),
  adminUsers: (t: TokenFn, limit = 100) =>
    call<{ users: AdminUser[] }>(t, `/api/admin/users?limit=${limit}`),
  adminCoverage: (t: TokenFn) =>
    call<{ summary: unknown }>(t, "/api/admin/coverage"),

  /**
   * The prize ledger and what it allows.
   *
   * `prize` and `winners` are the planning assumptions the projection is run against
   * — the answer to "how many more events" depends on them, so they are inputs rather
   * than something the server guesses.
   */
  adminPayouts: (t: TokenFn, opts: { year?: number; prize?: number; winners?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.year !== undefined) q.set("year", String(opts.year));
    if (opts.prize !== undefined) q.set("prize", String(opts.prize));
    if (opts.winners !== undefined) q.set("winners", String(opts.winners));
    const qs = q.toString();
    return call<PayoutLedger>(t, `/api/admin/payouts${qs ? `?${qs}` : ""}`);
  },
  /** `forever` grants a lifetime gift seat; otherwise a dated comp. */
  adminComp: (t: TokenFn, userId: string, opts: { forever: boolean; days?: number }) =>
    call<{ ok: boolean; until: number | null; forever: boolean }>(t, "/api/admin/comp", {
      method: "POST",
      body: JSON.stringify({ userId, forever: opts.forever, days: opts.days }),
    }),
  adminRevoke: (t: TokenFn, userId: string) =>
    call<{ ok: boolean; plan: string; revoked: number }>(t, "/api/admin/revoke", {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),
};

export function money(cents: number): string {
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/** One blackjack sitting as the server stores it. Snake case: it is a database row. */
export interface BlackjackSittingRow {
  id: string;
  started_at: number;
  ended_at: number;
  decks: number;
  dealer_stands_soft_17: number;
  seats: number;
  fidelity: "sharp" | "casual" | "loose";
  hands: number;
  decisions: number;
  correct: number;
  /** Bet units given up against the exact best action. Not an estimate. */
  ev_given: number;
  net: number;
}

export interface BlackjackTotals {
  sittings: number;
  hands: number;
  decisions: number;
  correct: number;
  ev_given: number;
  net: number;
}

export interface GameSessionRow {
  id: string;
  started_at: number;
  ended_at: number;
  seats: number;
  format: "cash" | "tournament";
  big_blind: number;
  stack_bb: number;
  difficulty: number;
  opponents: string;
  hands: number;
  net: number;
  decisions: number;
  ev_lost_est: number;
}

/** Summed across every session, in big blinds so stakes can be compared. */
export interface GameTotals {
  sessions: number;
  hands: number;
  net_bb: number;
  vpip: number; pfr: number;
  three_bet_chances: number; three_bets: number;
  faced_three_bet: number; folded_to_three_bet: number;
  saw_flop: number; showdowns: number; showdowns_won: number; won_saw_flop: number;
  aggressive: number; passive: number;
  cbet_chances: number; cbets: number; faced_cbet: number; folded_to_cbet: number;
  all_ins: number; decisions: number;
  ev_lost_bb: number;
  ev_preflop_bb: number; ev_flop_bb: number; ev_turn_bb: number; ev_river_bb: number;
}

/**
 * The segment shape lives with the code that orders and formats it — re-exported
 * above, so callers still import it from here. See the import at the top of the file.
 */

/**
 * What `/api/me/leaks` returns, in both shapes.
 *
 * The gated shape is not the full shape with fields blanked — the withheld segments
 * never leave the Worker. What arrives instead is the single worst leak plus counts
 * of what is behind the gate, which is enough for an honest upgrade prompt and no
 * more.
 */
export interface LeakReportResponse {
  windowDays: number;
  /** The floor a segment must clear before it gets a verdict. From the server. */
  minSample: number;
  gated: boolean;
  solvedSpots: number;
  /** Graded against a modelled chart. Never mixed into a headline number. */
  modelledSpots: number;
  /** Solved spots too old to carry a comparable cost. Accuracy still counts them. */
  unmeasuredSpots: number;
  segments: LeakSegment[];
  worst: LeakSegment | null;
  strengths: LeakSegment[];
  /** Gated responses only. */
  withheldSegments?: number;
  withheldStrengths?: number;
}

/** One person's prize total for a calendar year. */
export interface PayoutPerson {
  userId: string;
  year: number;
  awards: number;
  total: number;
  headroom: number;
  status: "clear" | "approaching" | "at_limit" | "over";
  email: string | null;
  displayName: string | null;
}

/**
 * What the prize ledger allows.
 *
 * Two event projections, never one. The answer to "how many more events can we run"
 * depends entirely on WHO wins them: the same prize pool goes much further spread
 * across a field than concentrated on one person. A single number would be a
 * confident answer to a question that does not have one.
 */
export interface PayoutLedger {
  year: number;
  threshold: number;
  warnFraction: number;
  assumptions: { prizePerEvent: number; winnersPerEvent: number; eligibleWithoutAwards: number };
  projection: {
    concentratedEvents: number;
    spreadEvents: number;
    totalHeadroom: number;
    /** 0 to 1. Nearer 1 means reality is closer to the concentrated bound. */
    concentration: number;
    blocked: number;
    approaching: number;
  };
  people: PayoutPerson[];
  note: string;
}
